import { gsap, ScrollTrigger } from './utils/gsap-config.js';
import DeviceDetector from './utils/device-detector.js';

class ScrollDataAnimation {
  constructor() {
    this.deviceDetector = DeviceDetector.getInstance();
    this.currentSlide = 0;
    this.totalSlides = 3;
    this.isAnimating = false;
    this.isActive = false;
    this.wheelAccumulator = 0;
    this.wheelThreshold = 100;
    this.sectionElement = null;
    this.originalBodyOverflow = '';
    this.originalBodyPaddingRight = '';
    this.boundHandleWheel = null;
    this.scrollHandler = null;
    this.checkCenterAlignment = null;
    this.scrollVelocity = 0;
    this.lastActivationTime = 0;
    this.fastScrollThreshold = 2;
    
    this.init();
  }

  init() {
    this.deviceDetector.onDesktop(() => {
      this.initScrollAnimation();
    });

    this.deviceDetector.onMobile(() => {
      this.destroy();
    });
    
    this.setupEmergencyScrollRestore();
  }
  
  setupEmergencyScrollRestore() {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && document.body.style.overflow === 'hidden' && !this.isActive) {
        document.body.style.overflow = this.originalBodyOverflow;
        document.body.style.paddingRight = this.originalBodyPaddingRight;
      }
    });
    
    window.addEventListener('focus', () => {
      if (document.body.style.overflow === 'hidden' && !this.isActive) {
        document.body.style.overflow = this.originalBodyOverflow;
        document.body.style.paddingRight = this.originalBodyPaddingRight;
      }
    });
  }

  initScrollAnimation() {
    this.sectionElement = document.getElementById('scroll-data-section');
    if (!this.sectionElement) return;
    
    this.setSlide(0, false);
    this.setupScrollBasedDetection();
  }

  setupScrollBasedDetection() {
    this.checkCenterAlignment = () => {
      if (!this.sectionElement) return;
      
      const rect = this.sectionElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportCenter = viewportHeight / 2;
      
      const sectionTop = rect.top;
      const sectionBottom = rect.bottom;
      const sectionHeight = rect.height;
      const sectionCenter = sectionTop + (sectionHeight / 2);
      
      const distanceFromCenter = Math.abs(sectionCenter - viewportCenter);
      
      const activationThreshold = viewportHeight * 0.08;
      const deactivationThreshold = viewportHeight * 0.15;
      const bufferZone = viewportHeight * 0.1;
      
      const isInViewport = sectionBottom > -bufferZone && sectionTop < viewportHeight + bufferZone;
      const shouldActivate = distanceFromCenter < activationThreshold && isInViewport;
      const shouldDeactivate = distanceFromCenter > deactivationThreshold || !isInViewport;
      
      if (shouldActivate && !this.isActive) {
        this.activateScrollControl();
      } else if (shouldDeactivate && this.isActive) {
        this.deactivateScrollControl();
      }
      
      if (distanceFromCenter < deactivationThreshold && isInViewport && !this.isActive) {
        this.prepareSectionForActivation();
      }
    };
    
    this.checkCenterAlignment();
    this.setupScrollListener();
  }
  
  prepareSectionForActivation() {
    if (!this.sectionElement) return;
    
    const contentElements = ['people-content', 'company-content', 'jobs-content'];
    const imageElements = ['tree-1', 'tree-2', 'tree-3'];
    
    [...contentElements, ...imageElements].forEach(id => {
      const element = document.getElementById(id);
      if (element) {
        element.getBoundingClientRect();
      }
    });
  }

  handleFastScrollDetection() {
    if (!this.sectionElement || this.isActive) return;
    
    const rect = this.sectionElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportCenter = viewportHeight / 2;
    
    if (this.scrollVelocity > this.fastScrollThreshold) {
      const sectionTop = rect.top;
      const sectionBottom = rect.bottom;
      const sectionCenter = sectionTop + (rect.height / 2);
      
      const distanceFromCenter = Math.abs(sectionCenter - viewportCenter);
      const activationThreshold = viewportHeight * 0.08;
      const bufferZone = viewportHeight * 0.1;
      
      const crossingViewport = sectionBottom > -bufferZone && sectionTop < viewportHeight + bufferZone;
      const nearCenter = distanceFromCenter < activationThreshold;
      
      if (crossingViewport && nearCenter) {
        this.activateScrollControl();
        
        setTimeout(() => {
          if (this.isActive && this.wheelAccumulator === 0) {
            const currentRect = this.sectionElement.getBoundingClientRect();
            const currentCenter = currentRect.top + currentRect.height/2;
            const currentDistance = Math.abs(currentCenter - viewportCenter);
            const deactivationThreshold = viewportHeight * 0.15;
            
            if (currentDistance > deactivationThreshold) {
              this.deactivateScrollControl();
            }
          }
        }, 200);
      }
    }
  }
  
  setupScrollListener() {
    let ticking = false;
    let lastScrollTime = 0;
    let scrollVelocity = 0;
    let lastScrollY = window.scrollY;
    
    this.scrollHandler = () => {
      const now = performance.now();
      const currentScrollY = window.scrollY;
      
      if (now - lastScrollTime > 0) {
        scrollVelocity = Math.abs(currentScrollY - lastScrollY) / (now - lastScrollTime);
      }
      
      lastScrollTime = now;
      lastScrollY = currentScrollY;
      
      this.scrollVelocity = scrollVelocity;
      
      if (!ticking) {
        requestAnimationFrame(() => {
          this.checkCenterAlignment();
          this.handleFastScrollDetection();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }

  activateScrollControl() {
    if (this.isActive) return;
    
    // Prevent too frequent activation/deactivation cycles
    const now = performance.now();
    if (now - this.lastActivationTime < 100) {
      return;
    }
    this.lastActivationTime = now;
    
    this.isActive = true;
    this.wheelAccumulator = 0;
    
    this.originalBodyOverflow = document.body.style.overflow || '';
    this.originalBodyPaddingRight = document.body.style.paddingRight || '';
    
    const scrollbarWidth = this.getScrollbarWidth();
    
    this.boundHandleWheel = this.handleWheel.bind(this);
    
    document.addEventListener('wheel', this.boundHandleWheel, { passive: false, capture: true });
    
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    
    this.sectionElement?.setAttribute('data-scroll-active', 'true');
  }

  deactivateScrollControl() {
    if (!this.isActive) return;
    
    this.isActive = false;
    
    if (this.boundHandleWheel) {
      document.removeEventListener('wheel', this.boundHandleWheel, { capture: true });
      this.boundHandleWheel = null;
    }
    
    document.body.style.overflow = this.originalBodyOverflow;
    document.body.style.paddingRight = this.originalBodyPaddingRight;
    
    this.wheelAccumulator = 0;
    
    this.sectionElement?.removeAttribute('data-scroll-active');
    
    // Multiple safety checks for scroll restoration
    const restoreScroll = () => {
      if (!this.isActive && document.body.style.overflow === 'hidden') {
        document.body.style.overflow = this.originalBodyOverflow;
        document.body.style.paddingRight = this.originalBodyPaddingRight;
      }
    };
    
    restoreScroll();
    setTimeout(restoreScroll, 50);
    setTimeout(restoreScroll, 150);
  }

  handleWheel(event) {
    if (!this.isActive) {
      event.preventDefault();
      return;
    }

    if (this.isAnimating) {
      event.preventDefault();
      return;
    }

    event.preventDefault();

    // Adjust threshold based on scroll velocity
    const baseThreshold = 100;
    const velocityMultiplier = this.scrollVelocity > 1 ? 0.7 : 1;
    const adjustedThreshold = baseThreshold * velocityMultiplier;

    this.wheelAccumulator += event.deltaY;

    if (Math.abs(this.wheelAccumulator) >= adjustedThreshold) {
      if (this.wheelAccumulator > 0) {
        this.handleScrollDown();
      } else {
        this.handleScrollUp();
      }
      
      this.wheelAccumulator = 0;
    }
  }

  handleScrollDown() {
    if (this.currentSlide < this.totalSlides - 1) {
      this.currentSlide++;
      this.setSlide(this.currentSlide, true);
    } else {
      this.exitSectionDown();
    }
  }

  handleScrollUp() {
    if (this.currentSlide > 0) {
      this.currentSlide--;
      this.setSlide(this.currentSlide, true);
    } else {
      this.exitSectionUp();
    }
  }

  exitSectionDown() {
    this.deactivateScrollControl();
    
    const restoreAndContinue = () => {
      if (document.body.style.overflow === 'hidden') {
        document.body.style.overflow = this.originalBodyOverflow;
        document.body.style.paddingRight = this.originalBodyPaddingRight;
      }
      
      const scrollAmount = Math.min(window.innerHeight * 0.15, 100);
      window.scrollBy({
        top: scrollAmount,
        behavior: 'auto'
      });
    };
    
    restoreAndContinue();
    setTimeout(restoreAndContinue, 16);
    setTimeout(restoreAndContinue, 50);
  }

  exitSectionUp() {
    this.deactivateScrollControl();
    
    const restoreAndContinue = () => {
      if (document.body.style.overflow === 'hidden') {
        document.body.style.overflow = this.originalBodyOverflow;
        document.body.style.paddingRight = this.originalBodyPaddingRight;
      }
      
      const scrollAmount = Math.min(window.innerHeight * 0.15, 100);
      window.scrollBy({
        top: -scrollAmount,
        behavior: 'auto'
      });
    };
    
    restoreAndContinue();
    setTimeout(restoreAndContinue, 16);
    setTimeout(restoreAndContinue, 50);
  }

  setSlide(slideIndex, animate = true) {
    if (this.isAnimating) return;
    
    this.isAnimating = true;

    const duration = animate ? 0.3 : 0;
    const ease = "power2.out";

    const contentElements = ['people-content', 'company-content', 'jobs-content'];

    if (!animate) {
      contentElements.forEach((id, index) => {
        const element = document.getElementById(id);
        if (!element) return;

        if (index === slideIndex) {
          gsap.set(element, { opacity: 1, x: 0 });
        } else {
          gsap.set(element, { opacity: 0, x: index < slideIndex ? -30 : 30 });
        }
      });
      this.animateLayeredImages(slideIndex, 0, ease);
      setTimeout(() => { this.isAnimating = false; }, 100);
      return;
    }

    const tl = gsap.timeline({
      onComplete: () => { this.isAnimating = false; }
    });

    contentElements.forEach((id, index) => {
      const element = document.getElementById(id);
      if (!element) return;

      if (index === slideIndex) {
        tl.fromTo(element, 
          { opacity: 0, x: index < this.currentSlide ? -30 : 30 },
          { opacity: 1, x: 0, duration: duration, ease: ease },
          0.25
        );
      } else {
        const currentOpacity = gsap.getProperty(element, "opacity");
        if (currentOpacity > 0) {
          tl.to(element, {
            opacity: 0,
            x: index < slideIndex ? -30 : 30,
            duration: duration,
            ease: ease
          }, 0);
        }
      }
    });

    this.animateLayeredImages(slideIndex, duration, ease, tl);
  }

  animateLayeredImages(slideIndex, duration, ease, tl) {
    const tree1 = document.getElementById('tree-1');
    const tree2 = document.getElementById('tree-2');
    const tree3 = document.getElementById('tree-3');
    
    if (!tree1 || !tree2 || !tree3) return;
    
    if (!tl) {
      gsap.set(tree1, { opacity: 1, x: 0 });
      
      switch (slideIndex) {
        case 0:
          gsap.set(tree2, { opacity: 0, x: 50 });
          gsap.set(tree3, { opacity: 0, x: -30 });
          break;
        case 1:
          gsap.set(tree2, { opacity: 1, x: 0 });
          gsap.set(tree3, { opacity: 0, x: -30 });
          break;
        case 2:
          gsap.set(tree2, { opacity: 1, x: 0 });
          gsap.set(tree3, { opacity: 1, x: 0 });
          break;
      }
      return;
    }
    
    tl.to(tree1, {
      opacity: 1,
      x: 0,
      duration: duration,
      ease: ease
    }, 0.25);
    
    switch (slideIndex) {
      case 0:
        const tree2CurrentOpacity = gsap.getProperty(tree2, "opacity");
        const tree3CurrentOpacity = gsap.getProperty(tree3, "opacity");
        
        if (tree2CurrentOpacity > 0) {
          tl.to(tree2, { opacity: 0, x: 50, duration: duration, ease: ease }, 0);
        }
        if (tree3CurrentOpacity > 0) {
          tl.to(tree3, { opacity: 0, x: -40, y:0, duration: duration, ease: ease }, 0);
        }
        break;
        
      case 1:
        const tree2Opacity1 = gsap.getProperty(tree2, "opacity");
        const tree3Opacity1 = gsap.getProperty(tree3, "opacity");
        
        if (tree2Opacity1 < 1) {
          tl.fromTo(tree2, 
            { opacity: 0, x: 0 },
            { opacity: 1, x: -40, duration: duration, ease: ease }, 
            0.25
          );
        }
        if (tree3Opacity1 > 0) {
          tl.to(tree2, { opacity: 1, x: -40, duration: duration, ease: ease }, 0);
          tl.to(tree3, { opacity: 0, x: -40, y:0, duration: duration, ease: ease }, 0);
        }
        break;
        
      case 2:
        const tree2Opacity2 = gsap.getProperty(tree2, "opacity");
        const tree3Opacity2 = gsap.getProperty(tree3, "opacity");
        
        if (tree2Opacity2 < 1) {
          tl.fromTo(tree2,
            { opacity: 0, x: 0 },
            { opacity: 1, x: -40, duration: duration, ease: ease },
            0.25
          );
        }
        if (tree3Opacity2 < 1) {
          tl.to(tree2, { opacity: 1, x: 0, duration: duration, ease: ease }, 0);
          tl.fromTo(tree3,
            { opacity: 0, x: 0, y: 0 },
            { opacity: 1, x: -40, y: 10, duration: duration, ease: ease },
            0.25
          );
        }
        break;
    }
  }

  destroy() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
    
    this.deactivateScrollControl();
    
    document.body.style.overflow = this.originalBodyOverflow;
    document.body.style.paddingRight = this.originalBodyPaddingRight;
    
    if (this.boundHandleWheel) {
      document.removeEventListener('wheel', this.boundHandleWheel);
      this.boundHandleWheel = null;
    }
    
    const allElements = [
      'people-content', 'company-content', 'jobs-content',
      'tree-1', 'tree-2', 'tree-3'
    ];
    
    allElements.forEach(id => {
      const element = document.getElementById(id);
      if (element) {
        gsap.set(element, { clearProps: "all" });
      }
    });
  }

  goToSlide(slideIndex) {
    if (slideIndex >= 0 && slideIndex < this.totalSlides && this.isActive) {
      this.currentSlide = slideIndex;
      this.setSlide(slideIndex, true);
    }
  }
}

export default ScrollDataAnimation; 